import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import feedbackSurveyService from '@/services/feedbackSurveyService.ts';
import type { FeedbackSurveySource } from '@/types/feedbackSurvey.ts';
import StarRating10 from './StarRating10.tsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  isOpen: boolean;
  source: FeedbackSurveySource;
  onClose: () => void;
  /** Chiamato dopo l'invio andato a buon fine (il banner deve sparire). */
  onCompleted: () => void;
}

interface Ratings {
  listeningRating: number;
  easeOfUseRating: number;
  wouldPayRating: number;
  employerProvidedRating: number;
  recommendRating: number;
}

const EMPTY_RATINGS: Ratings = {
  listeningRating: 0,
  easeOfUseRating: 0,
  wouldPayRating: 0,
  employerProvidedRating: 0,
  recommendRating: 0,
};

export default function FeedbackSurveyModal({ isOpen, source, onClose, onCompleted }: Props) {
  const { t } = useTranslation();
  const [ratings, setRatings] = useState<Ratings>(EMPTY_RATINGS);
  const [text, setText] = useState({
    missingExpected: '',
    likedMost: '',
    likedLeast: '',
    difficulties: '',
    wouldPayReason: '',
    otherComments: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const setRating = (key: keyof Ratings, value: number) =>
    setRatings((prev) => ({ ...prev, [key]: value }));

  const ratedCount = useMemo(
    () => Object.values(ratings).filter((v) => v > 0).length,
    [ratings],
  );
  const allRated = ratedCount === 5;

  const handleSubmit = async () => {
    if (!allRated) {
      setError(t('feedbackSurvey.errors.ratingsRequired'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await feedbackSurveyService.submit({
        source,
        ...ratings,
        missingExpected: text.missingExpected.trim() || undefined,
        likedMost: text.likedMost.trim() || undefined,
        likedLeast: text.likedLeast.trim() || undefined,
        difficulties: text.difficulties.trim() || undefined,
        wouldPayReason: text.wouldPayReason.trim() || undefined,
        otherComments: text.otherComments.trim() || undefined,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('feedbackSurvey.errors.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  // Alla chiusura del ringraziamento: notifica il completamento.
  const handleClose = () => {
    if (success) onCompleted();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent
        showCloseButton={!success}
        className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
      >
        {success ? (
          <div className="flex flex-col items-center px-8 py-12 text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary text-white shadow-lg shadow-primary/30">
              <Check className="size-8" strokeWidth={2.5} />
            </div>
            <DialogTitle className="mt-5 font-heading text-2xl text-foreground">
              {t('feedbackSurvey.thanksTitle')}
            </DialogTitle>
            <DialogDescription className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {t('feedbackSurvey.thanksMessage')}
            </DialogDescription>
            <Button className="mt-7 min-w-[160px]" onClick={handleClose}>
              {t('feedbackSurvey.close')}
            </Button>
          </div>
        ) : (
          <>
            {/* Header con gradiente brand */}
            <div className="relative shrink-0 bg-gradient-to-br from-primary to-secondary px-6 py-6 text-white sm:px-8">
              <DialogTitle className="font-heading text-2xl leading-tight sm:text-[1.7rem]">
                {t('feedbackSurvey.title')}
              </DialogTitle>
              <DialogDescription className="mt-1.5 max-w-lg text-sm leading-relaxed text-white/85">
                {t('feedbackSurvey.subtitle')}
              </DialogDescription>
            </div>

            {/* Corpo scrollabile */}
            <div className="flex-1 space-y-7 overflow-y-auto px-6 py-6 sm:px-8">
              <Section index={1} title={t('feedbackSurvey.section.experience')}>
                <StarRating10
                  label={t('feedbackSurvey.q.listening')}
                  value={ratings.listeningRating}
                  onChange={(v) => setRating('listeningRating', v)}
                />
                <StarRating10
                  label={t('feedbackSurvey.q.easeOfUse')}
                  value={ratings.easeOfUseRating}
                  onChange={(v) => setRating('easeOfUseRating', v)}
                />
              </Section>

              <Section index={2} title={t('feedbackSurvey.section.words')}>
                <OpenField
                  label={t('feedbackSurvey.q.missingExpected')}
                  value={text.missingExpected}
                  onChange={(v) => setText((p) => ({ ...p, missingExpected: v }))}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <OpenField
                    label={t('feedbackSurvey.q.likedMost')}
                    value={text.likedMost}
                    onChange={(v) => setText((p) => ({ ...p, likedMost: v }))}
                  />
                  <OpenField
                    label={t('feedbackSurvey.q.likedLeast')}
                    value={text.likedLeast}
                    onChange={(v) => setText((p) => ({ ...p, likedLeast: v }))}
                  />
                </div>
                <OpenField
                  label={t('feedbackSurvey.q.difficulties')}
                  value={text.difficulties}
                  onChange={(v) => setText((p) => ({ ...p, difficulties: v }))}
                />
              </Section>

              <Section index={3} title={t('feedbackSurvey.section.future')}>
                <StarRating10
                  label={t('feedbackSurvey.q.wouldPay')}
                  value={ratings.wouldPayRating}
                  onChange={(v) => setRating('wouldPayRating', v)}
                />
                <OpenField
                  label={t('feedbackSurvey.q.wouldPayReason')}
                  value={text.wouldPayReason}
                  onChange={(v) => setText((p) => ({ ...p, wouldPayReason: v }))}
                />
                <StarRating10
                  label={t('feedbackSurvey.q.employerProvided')}
                  value={ratings.employerProvidedRating}
                  onChange={(v) => setRating('employerProvidedRating', v)}
                />
                <StarRating10
                  label={t('feedbackSurvey.q.recommend')}
                  value={ratings.recommendRating}
                  onChange={(v) => setRating('recommendRating', v)}
                />
                <OpenField
                  label={t('feedbackSurvey.q.other')}
                  value={text.otherComments}
                  onChange={(v) => setText((p) => ({ ...p, otherComments: v }))}
                />
              </Section>

              {error && (
                <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
            </div>

            {/* Footer sticky: solo il bottone d'invio, centrato e in grassetto */}
            <div className="flex shrink-0 items-center justify-center border-t border-border bg-card px-6 py-4 sm:px-8">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="min-w-[200px] font-bold"
              >
                {submitting && <Loader2 className="size-4 animate-spin" />}
                {t('feedbackSurvey.submit')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2.5">
        <span className="flex size-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
          {index}
        </span>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

function OpenField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-foreground">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        maxLength={4000}
        className="w-full resize-none rounded-xl border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </div>
  );
}
